import {
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SignalSlashIcon,
} from '@heroicons/react/24/outline';
import type {
  tAlertCta,
  tAlertVariant,
  tLiveAlert,
} from 'src/alerts/AlertCenter.types';
import { ALERT_CENTER_NS } from 'src/alerts/AlertCenter.constants';
import {
  alertBodyKeys,
  withActionableCta,
} from 'src/alerts/AlertCenter.functions';
import {
  ALERT_CENTER_EVENTS,
  type tAlertBannerCtaClickedProps,
  type tAlertBannerShownProps,
  type tAlertCriticalPromotedProps,
} from 'src/hooks/analytics/alertCenterAnalytics.constants';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { useAlertCenterContext } from 'src/providers/AlertCenterProvider/AlertCenter.hook';
import { useFeatureFlagsContext } from 'src/providers/FeatureFlagsProvider/FeatureFlags.context';
import AlertCard from './AlertCard.component';
import type { tAlertCardAction } from './AlertCard.component';
import { ALERT_TIER_ICON } from './AlertCenter.icons';
import { useAlertCta } from './AlertCenter.cta.hook';
import { useAlertDetailHost } from './AlertDetailHost.hook';
import AlertDetailModal from './AlertDetailModal.component';
import {
  closeAlertForSession,
  isAlertClosedForSession,
} from './AlertInPage.session';
import './AlertBanner.styles.scss';

/** The analytics `source` this surface registers its alert under (R9). */
export const WIDE_BANNER_SOURCE = 'wide';

/**
 * The CSS variable this surface publishes its height under (INT-1653). Pages
 * that fill "everything below the navbar" subtract it too, now that the
 * critical card sits at the top of the content column instead of inside the
 * navbar shell that `--LayoutPrivate-nav-height` measures.
 */
export const ALERT_BANNER_HEIGHT_VAR = '--LayoutPrivate-alert-height';

/**
 * The glyph a P0 takes when the tier's warning triangle would say the wrong
 * thing. A disconnected number is a lost signal, not a generic warning.
 */
const WIDE_ALERT_ICON: Partial<Record<string, typeof SignalSlashIcon>> = {
  'wapi-disconnected': SignalSlashIcon,
};

/**
 * INT-1653 — an alert that declares its own variant takes that variant's
 * glyph (the disconnection notices span all four: lost signal, a warning, a
 * reconnection in progress, back online).
 */
const VARIANT_ICON: Record<tAlertVariant, typeof SignalSlashIcon> = {
  error: SignalSlashIcon,
  warning: ExclamationTriangleIcon,
  info: ArrowPathIcon,
  success: CheckCircleIcon,
};

function resolveIcon(alert: tLiveAlert): typeof SignalSlashIcon {
  if (alert.copy.variant) return VARIANT_ICON[alert.copy.variant];
  return WIDE_ALERT_ICON[alert.id] ?? ALERT_TIER_ICON[alert.priority];
}

/**
 * The critical banner (RET-1876, D4; redesigned in INT-1653) — ONE P0 at a
 * time, per viewer, arbitrated by `windowRank` in the provider. Renders
 * nothing at all while no critical is live: absent from the DOM, never
 * CSS-hidden (KTD14).
 *
 * INT-1653 (Producto, 2026-10-19): it is no longer the full-width strip above
 * the sidebar. It is an `AlertCard` at the top of the content column, the same
 * anatomy the in-page surface uses, on every module. Its height still reaches
 * the viewport-bound pages, through `ALERT_BANNER_HEIGHT_VAR`.
 *
 * G13 (anti-flash): the card renders only once the copy namespace is loaded
 * AND a winner exists — one reflow, never raw keys on the most prominent
 * element in the system. G12 (promotion): the provider's arbitration is pure
 * and synchronous, so a resolved winner and its successor swap in the same
 * render pass; the successor's content fades in (CSS only) and a resolved P0
 * is never animated out.
 */
export default function AlertBanner() {
  const { t, i18n } = useTranslation(ALERT_CENTER_NS);
  const { wideAlert, waitingCriticalAlerts, registerInlineAlert } =
    useAlertCenterContext();
  // Analytics gate on the REAL flag, not `areSurfacesEnabled`: with the dev
  // simulator override active on a flag-off workspace the strip must render
  // (that is the local QA path), but a PostHog event from it would be a
  // phantom — the schema's rule is "events fire only while the flag is ON".
  const { isAlertCenterEnabled } = useFeatureFlagsContext();
  const { trackEvent } = useAnalytics();
  /*
   * This surface hosts its own open detail, like the panel and the in-page
   * banner. It did not, and the gap had a consequence: a `detail` CTA declared
   * on a `wide` alert clicked dead, so `la-usage-capped` — a P0 whose whole ask
   * is "amplía tu tier" — could only NAVIGATE, and the page it navigated to
   * carries no upgrade affordance. Injecting the opener is what lets the
   * critical strip carry a checkout instead of a detour.
   *
   * Fallback focus: the strip disappears the moment its critical resolves
   * (possibly while the detail is open), so the bell is where the alert then
   * lives and where focus belongs.
   */
  const detail = useAlertDetailHost({
    fallbackFocus: () =>
      document.querySelector<HTMLElement>('[data-testid="alert-bell"]'),
  });
  const runCta = useAlertCta({ onOpenDetail: detail.open });

  // INT-1653 — a card the user closed (only the ones whose copy is
  // `closable`, the 8c reconnection notices) stays down until the next load.
  // Read at render time, like the in-page surface does.
  const [, onSessionClose] = useReducer((tick: number) => tick + 1, 0);
  const winner =
    wideAlert && !isAlertClosedForSession(wideAlert.key)
      ? withActionableCta(wideAlert)
      : null;
  const winnerKey = winner?.key ?? null;

  // INT-1653 — publish the card's height (its bottom spacing included) for the
  // pages that size themselves against the viewport. Layout effect, so the
  // first painted frame already has the page shrunk around the card.
  const shellRef = useRef<HTMLDivElement>(null);
  const isCardRendered =
    winnerKey !== null && i18n.hasLoadedNamespace(ALERT_CENTER_NS);
  useLayoutEffect(() => {
    const node = shellRef.current;
    if (!isCardRendered || !node) return undefined;

    const root = document.documentElement;
    const publishHeight = () => {
      root.style.setProperty(
        ALERT_BANNER_HEIGHT_VAR,
        `${node.getBoundingClientRect().height}px`,
      );
    };

    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(node);

    return () => {
      observer.disconnect();
      root.style.removeProperty(ALERT_BANNER_HEIGHT_VAR);
    };
  }, [isCardRendered, winnerKey]);

  // R9 — claim the rendered critical so the bell badge does not count it.
  // The waiting list is deliberately NOT registered: staying countable is how
  // it remains reachable from the bell instead of disappearing.
  // Depends on `winnerKey`, the rendered identity — `winner` itself is a
  // fresh object per render and would re-register in a loop.
  useEffect(() => {
    if (!winnerKey || !winner) return undefined;
    return registerInlineAlert(winner.id, WIDE_BANNER_SOURCE);
  }, [winnerKey, registerInlineAlert]);

  // ── Analytics ─────────────────────────────────────────────────────────────
  // Both events stamp point-in-time state (waiting_count), so they emit from
  // the effect that runs when the strip ACTUALLY rendered a winner — the
  // arbitration is synchronous over the already-resolved selection, so at
  // that moment the stamped values are the ones on screen (data-loaded rule).
  const lastShownKeyRef = useRef<string | null>(null);
  const incumbentIdRef = useRef<string | null>(null);
  const waitingCount = waitingCriticalAlerts.length;

  useEffect(() => {
    if (!winnerKey || !winner) {
      // The strip emptied: the next appearance is a fresh show, and its
      // promotion event reports `from: null` (the slot was empty).
      lastShownKeyRef.current = null;
      incumbentIdRef.current = null;
      return;
    }
    if (lastShownKeyRef.current === winnerKey) return;
    lastShownKeyRef.current = winnerKey;

    const previousIncumbentId = incumbentIdRef.current;
    incumbentIdRef.current = winner.id;
    if (!isAlertCenterEnabled) return;

    const shownProps: tAlertBannerShownProps = {
      alert_id: winner.id,
      window_rank: winner.windowRank ?? -1,
      waiting_count: waitingCount,
      ...winner.copy.analytics,
    };
    trackEvent(ALERT_CENTER_EVENTS.BANNER_SHOWN, shownProps);

    const promotedProps: tAlertCriticalPromotedProps = {
      from_alert_id: previousIncumbentId,
      to_alert_id: winner.id,
    };
    trackEvent(ALERT_CENTER_EVENTS.CRITICAL_PROMOTED, promotedProps);
    // `waitingCount` is a stamp, not a trigger: a waiting-list change without
    // a winner change must not re-emit "shown" for the same appearance.
  }, [winnerKey, isAlertCenterEnabled, trackEvent]);

  // ── Announcement (R10a) ───────────────────────────────────────────────────
  // Filled from an effect rather than rendered pre-populated, so assistive
  // tech sees a CHANGE inside a mounted live region — the case it reliably
  // announces (same pattern the panel's sibling surfaces use).
  const [announcement, setAnnouncement] = useState('');
  useEffect(() => {
    if (!winnerKey || !winner) {
      setAnnouncement((previous) => (previous === '' ? previous : ''));
      return;
    }
    setAnnouncement(t(winner.copy.titleKey, winner.copy.values ?? {}));
    // Re-announces per rendered identity (`winnerKey`), not per object.
  }, [winnerKey, t]);

  // G13 — one reflow: nothing renders until the namespace and a winner are
  // both ready. `hasLoadedNamespace` guards the raw-keys flash on the most
  // prominent element in the system.
  const openDetail = detail.alert ? (
    <AlertDetailModal alert={detail.alert} onClose={detail.close} />
  ) : null;

  // The card and its detail have DIFFERENT lifetimes (the RET-1957 lesson from
  // the in-page surface): a P0 can resolve while the Owner is inside the
  // checkout it opened, and an early return keyed on the strip would unmount
  // that checkout mid-charge. The strip goes with its winner; the open detail
  // outlives it and closes only by the user's hand.
  if (!winner || !i18n.hasLoadedNamespace(ALERT_CENTER_NS)) return openDetail;

  const { copy } = winner;
  const title = t(copy.titleKey, copy.values ?? {});
  const bodyKeys = alertBodyKeys(winner);

  const trackCta = (ctaKind: tAlertBannerCtaClickedProps['cta_kind']) => {
    if (!isAlertCenterEnabled) return;
    const ctaProps: tAlertBannerCtaClickedProps = {
      alert_id: winner.id,
      cta_kind: ctaKind,
      ...copy.analytics,
    };
    trackEvent(ALERT_CENTER_EVENTS.BANNER_CTA_CLICKED, ctaProps);
  };

  // INT-1653 — a support CTA opens the chat with the notice and Meta's cause
  // already written; the message needs `t`, so it is resolved here.
  const withMessage = (cta: NonNullable<tAlertCta>): NonNullable<tAlertCta> =>
    cta.messageKey
      ? {
          ...cta,
          message: t(cta.messageKey, { ...(copy.values ?? {}), title }),
        }
      : cta;

  const toAction = (
    cta: tAlertCta | undefined,
    testId: string,
  ): tAlertCardAction | null => {
    if (!cta) return null;
    const ready = withMessage(cta);
    const ctaKind = (ready.analyticsKind ?? ready.kind) as
      tAlertBannerCtaClickedProps['cta_kind'];
    // An external destination is a real link (new tab): the anchor opens it,
    // so the dispatch must not open it a second time.
    if (ready.kind === 'external' && ready.target) {
      return {
        label: t(ready.labelKey),
        testId,
        href: ready.target,
        onClick: () => trackCta(ctaKind),
      };
    }
    return {
      label: t(ready.labelKey),
      testId,
      onClick: () => {
        trackCta(ctaKind);
        // The secondary button runs through the same dispatch as the
        // primary, with its own CTA in the copy's place.
        runCta({ ...winner, copy: { ...copy, cta: ready } });
      },
    };
  };

  const supportLink = copy.inlineSupport;
  const descriptions = bodyKeys.map((bodyKey, index) => {
    // INT-1653 — one body line per `ls-alert-description` now that the
    // banner is a card in the content column, no longer a one-sentence strip
    // in the navbar. Two account-health bodies ship an inline link as <0>…</0>
    // Trans markup (pinned by `AlertCenter.i18n.test.ts`); the anchor still
    // degrades to emphasis, because the card's button carries the action.
    const line = (
      <Trans
        key={bodyKey}
        t={t}
        i18nKey={bodyKey}
        values={copy.values ?? {}}
        components={[<span key="0" className="font-semibold" />]}
      />
    );
    const isLast = index === bodyKeys.length - 1;
    if (!supportLink || !isLast) return line;
    // The third action goes in the text (DS: at most two buttons).
    const supportAction = toAction(supportLink.cta, 'alert-banner-support');
    return (
      <span key={bodyKey}>
        {line} {t(supportLink.leadKey)}{' '}
        <button
          type="button"
          className="ls-alert-action"
          data-testid="alert-banner-support"
          onClick={supportAction?.onClick}
        >
          {t(supportLink.labelKey)}
        </button>
      </span>
    );
  });

  return (
    <>
      <div ref={shellRef} className="AlertBanner">
        {/* Keyed by the winner's identity: a promotion swaps the node, so the
          successor fades in while the resolved P0 simply stops being (G12 —
          never animate the exit of a resolved critical). */}
        <AlertCard
          key={winner.key}
          className="AlertBanner__Card"
          variant={copy.variant ?? 'error'}
          icon={resolveIcon(winner)}
          title={title}
          titleTestId="alert-banner-title"
          descriptionTestId="alert-banner-body"
          dataAttributes={{
            'data-testid': 'alert-banner',
            'data-alert-id': winner.id,
            'data-window-rank': winner.windowRank ?? undefined,
            'data-waiting-count': waitingCount,
          }}
          descriptions={descriptions}
          note={
            copy.note
              ? {
                  text: t(copy.note.key, copy.values ?? {}),
                  locked: copy.note.locked,
                  testId: 'alert-banner-note',
                }
              : null
          }
          primaryAction={toAction(copy.cta, 'alert-banner-cta')}
          secondaryAction={toAction(
            copy.secondaryCta,
            'alert-banner-secondary-cta',
          )}
          close={
            copy.closable
              ? {
                  label: t('wapiDisconnection.close'),
                  testId: 'alert-banner-close',
                  onClose: () => {
                    trackCta('dismiss');
                    closeAlertForSession(winner.key);
                    onSessionClose();
                  },
                }
              : null
          }
        />

        <div
          className="sr-only"
          aria-live="assertive"
          aria-atomic="true"
          data-testid="alert-banner-live-region"
        >
          {announcement}
        </div>
      </div>
      {openDetail}
    </>
  );
}
